/* CHECKOUT: only the purchase button inside the offer card links here. */
export const CHECKOUT_URL = "https://customer-service-training-ready.impultienda.ar/checkout";

export const product = {
  name: "Customer Service Team Training",
  subtitle: "10 Ready-to-Run Sessions for Difficult Customer Conversations",
  brand: "Customer Service Training Ready",
  price: "27",
  currency: "USD",
};

export const stats = [
  { value: "10", label: "ready-to-run sessions" },
  { value: "30 min", label: "per session" },
  { value: "5", label: "bonus resources" },
  { value: "$27 USD", label: "one-time payment" },
];

export const sessions = [
  { title: "Start an Upset Conversation Without Making It Worse", text: "Practice an opening that acknowledges frustration and starts useful fact-finding." },
  { title: "Clarify the Problem Without Making the Customer Repeat Everything", text: "Summarize existing notes and ask for the missing details." },
  { title: "Explain a Delay and Set a Realistic Next Update", text: "Separate confirmed facts from uncertainty and agree on an update." },
  { title: "Respond to a Service Mistake With Clear Next Steps", text: "Acknowledge the verified error and explain authorized options." },
  { title: "Say No Without Ending the Conversation", text: "Set a clear policy boundary and offer a realistic alternative." },
  { title: "Handle Repeated Demands and Interruptions", text: "Restate a limit calmly and guide the conversation to a choice." },
  { title: "Make a Handoff Without Passing the Problem Around", text: "Transfer the relevant facts, owner and next step." },
  { title: "Write Email and Chat Replies That Sound Clear and Human", text: "Rewrite cold or defensive language into a useful response." },
  { title: "Follow Up When the Complaint Is Still Open", text: "Give an honest update and keep ownership visible." },
  { title: "Recognize When and How to Escalate", text: "Identify authority limits and prepare an actionable handover." },
];

export const situations = [
  {
    label: "Customers are upset before we start",
    sessionIndex: 0,
    prompt: "Help agents open calmly when the customer arrives already frustrated.",
    practice: "A calm opening that acknowledges the concern and moves to the next useful question.",
  },
  {
    label: "We struggle to say no",
    sessionIndex: 4,
    prompt: "Give agents a way to hold a policy line while keeping the conversation going.",
    practice: "Explaining a policy limit and offering an authorized alternative.",
  },
  {
    label: "Customers keep repeating their story",
    sessionIndex: 1,
    prompt: "Help agents use what is already on record so customers are not asked twice.",
    practice: "Using existing notes, summarizing the issue and asking only for missing details.",
  },
  {
    label: "Our replies sound cold",
    sessionIndex: 7,
    prompt: "Help agents write replies that read as clear, human and useful.",
    practice: "Rewriting a defensive message with acknowledgment, clarity and a next step.",
  },
];

export const workflowSteps = [
  { title: "Choose a skill", text: "Pick the conversation your team most needs to practice right now." },
  { title: "Review and adapt", text: "Read the session and match the case to your policies, authority limits and update times." },
  { title: "Run the practice", text: "Use the timed agenda, facilitator wording and role cards to guide 30 minutes." },
  { title: "Follow up on one behavior", text: "Agree on one action and check it on the next shift or in a huddle." },
];

export const sessionAssets = [
  { title: "Timed agendas", text: "A 30-minute plan from the opening to the action commitment." },
  { title: "Facilitator scripts", text: "Wording for the opening, demonstration, briefing and debrief." },
  { title: "Customer, agent & observer roles", text: "Role cards built around a fictional case for each session." },
  { title: "Model responses", text: "Example replies that show what a useful answer sounds like." },
  { title: "Observation sheets", text: "Specific checks observers use to give focused feedback." },
  { title: "Follow-up actions", text: "One behavior to apply and review after the session." },
];

export type BonusTone = "navy" | "teal" | "blue";

export const bonuses: { title: string; benefit: string; text: string; tone: BonusTone }[] = [
  {
    title: "50 Customer Service Phrases for Difficult Conversations",
    benefit: "Find useful wording when agents get stuck.",
    text: "A phrase bank for acknowledgment, boundaries, handoffs and follow-up.",
    tone: "blue",
  },
  {
    title: "30 Five-Minute Customer Service Team Huddles",
    benefit: "Keep the practice going between sessions.",
    text: "Short, focused team activities to reinforce skills between the core sessions.",
    tone: "teal",
  },
  {
    title: "Customer Service Training Needs & Skill Check Kit",
    benefit: "Choose the first skill to train.",
    text: "Self-checks, observations and practice cases to help you decide where to start.",
    tone: "navy",
  },
  {
    title: "The 90-Day Customer Service Practice Calendar",
    benefit: "Schedule the ten core sessions.",
    text: "Plan the core sessions and the reinforcement around them across 90 days.",
    tone: "teal",
  },
  {
    title: "The Customer Service Facilitator Rescue Kit",
    benefit: "Know what to say when a session goes off plan.",
    text: "For when role-play stalls, feedback becomes defensive or time runs short.",
    tone: "blue",
  },
];

export const offerIncludes = [
  { title: "10 facilitator-led 30-minute sessions", text: "One for each difficult conversation your team faces, from upset openings to escalation." },
  { title: "Timed agendas & facilitator wording", text: "What to say and when, from the opening to the action commitment." },
  { title: "Fictional cases with customer, agent & observer roles", text: "Everyone in the room has something to do, every round." },
  { title: "Model responses", text: "Example wording that shows the team what a useful reply sounds like." },
  { title: "Observation checklists", text: "Specific behaviors to watch for, so feedback is concrete, not vague." },
  { title: "10 participant worksheets & follow-up actions", text: "Give each participant a place to write, improve and commit to one behavior after practice." },
];

export const offerFacts = [
  { value: "10", label: "complete sessions" },
  { value: "300", label: "minutes of planned practice" },
  { value: "5", label: "bonus resources" },
  { value: "3", label: "channels: phone, email & chat" },
];

export const priceCardIncludes = [
  "Complete 88-page facilitator guide",
  "All 10 ready-to-run sessions",
  "All 5 bonus resources",
  "Printable participant materials",
  "Spoken & written practice",
];

export const faqs = [
  { q: "Can I print materials for my team?", a: "Yes. You may print and share the participant pages within your own internal team for facilitated practice. Keep the guide within your organization; resale and public redistribution are not included." },
  { q: "Is there a guarantee?", a: "Yes. Your purchase is covered by a 10-day money-back guarantee. Review the kit and contact us within 10 days of purchase if it is not right for your team. Use the support contact in your order confirmation to request a refund." },
  {
    q: "What exactly do I receive?",
    a: "The complete 88-page facilitator PDF with ten 30-minute sessions, 30 role cards, 10 practice worksheets, model responses, observation checklists and follow-up actions, plus the five listed bonus PDFs. All materials are in English.",
  },
  {
    q: "Who is it for?",
    a: "Supervisors, team leaders and internal trainers facilitating practice with customer service agents. It is not a self-paced video course for individual customers.",
  },
  {
    q: "Do I need to be an experienced trainer?",
    a: "Scripts, timings and instructions provide structure. You still review the session and adapt it to your team and company rules.",
  },
  {
    q: "How long are the sessions?",
    a: "Each core session is 30 minutes. The five-minute huddles are separate bonus reinforcement activities.",
  },
  {
    q: "Can I run only one session?",
    a: "Yes. Choose by current need, or follow the sequence.",
  },
  {
    q: "Does it work for phone, email and chat?",
    a: "It includes spoken and written practice, with channel adaptations. Use examples appropriate for your team.",
  },
  {
    q: "Do I need slides or special software?",
    a: "No slide decks are included or required. Use the facilitator guide and print or share the participant materials. Remote sessions require your usual meeting setup.",
  },
  {
    q: "Does this replace our service policies?",
    a: "No. Confirm authority limits, verification, escalation routes and response commitments before facilitating. No certification is included.",
  },
  {
    q: "Are the advanced programs included?",
    a: "The $27 offer includes the main ten-session kit and the five listed bonuses. Any additional checkout offers are separate and optional.",
  },
];
